import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { getAllItems, createItem, updateItem, deleteItem } from '../utils/itemOperations';
import { generateQRCodeUrl, generateQRCodeDataUrl, downloadQRCode, printQRCode } from '../utils/qrCode';
import QRCodeSizeSelector from '../components/qrcode/QRCodeSizeSelector';
import SupplyManagementDashboard from '../components/consumables/SupplyManagementDashboard';

const AdminPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    status: 'Available',
    itemType: 'equipment', // 'equipment' or 'consumable'
    initialStock: '',
    currentStock: '',
    unit: '',
    lowStockAlert: '',
    costPerUnit: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [showQrSizeSelector, setShowQrSizeSelector] = useState(false);
  const [qrCodeAction, setQrCodeAction] = useState(null); // 'download' or 'print'
  const [currentQrItem, setCurrentQrItem] = useState(null);
  const [activeTab, setActiveTab] = useState('items'); // 'items' or 'supplies'
  const navigate = useNavigate();
  const batchQrCodesRef = useRef([]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        const fetchedItems = await getAllItems();
        setItems(fetchedItems);
      } catch (error) {
        console.error('Error fetching items:', error);
        setError('Failed to load items. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      description: '',
      status: 'Available',
      itemType: 'equipment',
      initialStock: '',
      currentStock: '',
      unit: '',
      lowStockAlert: '',
      costPerUnit: ''
    });
    setImageFile(null);
    setSelectedItem(null);
  };

  const openForm = (item = null) => {
    if (item) {
      setFormData({
        name: item.name || '',
        category: item.category || '',
        description: item.description || '',
        status: item.status || 'Available',
        itemType: item.itemType || 'equipment',
        initialStock: item.initialStock || '',
        currentStock: item.currentStock || '',
        unit: item.unit || '',
        lowStockAlert: item.lowStockAlert || '',
        costPerUnit: item.costPerUnit || ''
      });
      setSelectedItem(item);
    } else {
      resetForm();
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name) {
      return setError('Item name is required');
    }
    
    // Consumable-specific validation
    if (formData.itemType === 'consumable') {
      if (!formData.initialStock || formData.initialStock <= 0) {
        return setError('Initial stock is required and must be greater than 0 for consumables');
      }
      if (!formData.currentStock || formData.currentStock < 0) {
        return setError('Current stock is required and cannot be negative for consumables');
      }
      if (!formData.unit || formData.unit.trim() === '') {
        return setError('Unit of measurement is required for consumables');
      }
      if (parseFloat(formData.currentStock) > parseFloat(formData.initialStock)) {
        return setError('Current stock cannot be greater than initial stock');
      }
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      if (selectedItem) {
        // Update existing item
        await updateItem(selectedItem.id, formData, imageFile);
        setSuccess('Item updated successfully!');
      } else {
        // Create new item
        await createItem(formData, imageFile);
        setSuccess('Item created successfully!');
      }
      
      // Refresh items list
      const updatedItems = await getAllItems();
      setItems(updatedItems);
      
      // Close form immediately to prevent UI getting stuck
      closeForm();
      
      // If new item created, generate QR code asynchronously after a small delay
      if (!selectedItem) {
        // Find the newly created item (should be the most recent one with matching name)
        const newItem = updatedItems.find(item => item.name === formData.name);
        
        // If we found the new item, add a manual QR download button instead of automatic download
        if (newItem) {
          // Set success message with instructions
          setSuccess('Item created successfully! You can see it in the list below.');
        }
      }
    } catch (error) {
      console.error('Error saving item:', error);
      setError('Failed to save item: ' + (error.message || 'Unknown error'));
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    // Find the item to get its details for the confirmation
    const item = items.find(i => i.id === itemId);
    
    let confirmMessage = 'Are you sure you want to delete this item? This action cannot be undone.';
    
    if (item && item.status === 'Checked Out') {
      confirmMessage = `WARNING: This item is currently checked out to ${item.currentHolderName || 'a student'}.\n\nDeleting it will automatically remove it from their checked-out items list.\n\nAre you sure you want to delete this item? This action cannot be undone.`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      await deleteItem(itemId);
      
      // Refresh items list
      const updatedItems = await getAllItems();
      setItems(updatedItems);
      
      setSuccess('Item deleted successfully!');
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('Failed to delete item: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Download QR code as image
  const handleDownloadQRCode = (item) => {
    setCurrentQrItem(item);
    setQrCodeAction('download');
    setShowQrSizeSelector(true);
  };
  
  const handlePrintQRCode = (item) => {
    setCurrentQrItem(item);
    setQrCodeAction('print');
    setShowQrSizeSelector(true);
  };
  
  const handleBatchPrintQRCode = (items, e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    if (items.length === 0) {
      setError('Please select at least one item to print');
      return;
    }
    batchQrCodesRef.current = items;
    setQrCodeAction('batch-print');
    setShowQrSizeSelector(true);
  };
  
  const handleGenerateQRCode = async (options) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const { width, height, unit, displayWidth, displayHeight } = options;
      
      if (qrCodeAction === 'download' && currentQrItem) {
        // Generate and download single QR code
        const qrDataUrl = await generateQRCodeDataUrl(
          currentQrItem.id, 
          currentQrItem.name, 
          width, 
          height,
          options.qrType || 'standard'  // Pass qrType to generate correct QR code type
        );
        
        const sanitizedName = currentQrItem.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const fileName = `qrcode_${sanitizedName}_${displayWidth}x${displayHeight}${unit}`;
        
        await downloadQRCode(qrDataUrl, fileName, width, height);
        setSuccess('QR code downloaded successfully!');
        
      } else if (qrCodeAction === 'print' && currentQrItem) {
        // Print single QR code
        await printQRCode(currentQrItem, width, height, unit);
        setSuccess('QR code sent to printer!');
        
      } else if (qrCodeAction === 'batch-print' && batchQrCodesRef.current.length > 0) {
        // Handle batch printing
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
          throw new Error('Pop-up blocked! Please allow pop-ups for this site to print QR codes.');
        }
        
        // Generate HTML for all QR codes in a grid
        const qrGrid = await Promise.all(
          batchQrCodesRef.current.map(async (item) => {
            const qrDataUrl = await generateQRCodeDataUrl(item.id, item.name, width, height);
            return `
              <div class="qr-item" style="
                display: inline-block;
                width: ${displayWidth}${unit};
                height: ${displayHeight}${unit};
                margin: 0.2${unit};
                page-break-inside: avoid;
                break-inside: avoid;
                text-align: center;
                padding: 0.2${unit};
              ">
                <img 
                  src="${qrDataUrl}" 
                  alt="QR Code for ${item.name}" 
                  style="
                    max-width: 100%;
                    max-height: 80%;
                    object-fit: contain;
                  "
                />
                <div style="
                  font-size: 0.8em;
                  margin-top: 0.2${unit};
                  word-break: break-word;
                  overflow: hidden;
                  text-overflow: ellipsis;
                ">
                  ${item.name}
                </div>
              </div>
            `;
          })
        );
        
        const printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Batch QR Codes</title>
            <style>
              @page {
                size: letter;
                margin: 0.5cm;
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0.5cm;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .qr-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(${displayWidth}${unit}, 1fr));
                gap: 0.2${unit};
                width: 100%;
              }
              .qr-item {
                border: 1px solid #eee;
                border-radius: 4px;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 0.2${unit};
              }
              @media print {
                .no-print {
                  display: none;
                }
                body {
                  padding: 0;
                }
              }
            </style>
          </head>
          <body>
            <div class="qr-grid">
              ${qrGrid.join('\n')}
            </div>
            <div class="no-print" style="text-align: center; margin-top: 20px;">
              <button onclick="window.print()" style="
                padding: 10px 20px;
                background: #4CAF50;
                color: white;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 16px;
              ">
                Print All QR Codes
              </button>
            </div>
            <script>
              // Auto-print when the window loads
              window.onload = function() {
                setTimeout(() => {
                  window.print();
                }, 500);
              };
            </script>
          </body>
          </html>
        `;
        
        printWindow.document.open();
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        setSuccess(`Prepared ${batchQrCodesRef.current.length} QR codes for printing!`);
      }
      
    } catch (error) {
      console.error('Error processing QR code:', error);
      setError('Failed to process QR code: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
      setShowQrSizeSelector(false);
      setCurrentQrItem(null);
      batchQrCodesRef.current = [];
    }
  };

  const toggleBatchMode = () => {
    setIsBatchMode(!isBatchMode);
    if (isBatchMode) {
      setSelectedItems([]);
    }
  };

  const toggleItemSelection = (item, e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setSelectedItems(prev => {
      const isSelected = prev.some(selected => selected.id === item.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="bg-black px-6 py-4">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
              {activeTab === 'items' && (
                <div className="flex space-x-2">
                  <button
                    onClick={toggleBatchMode}
                    className={`px-4 py-2 rounded-md ${isBatchMode ? 'bg-blue-600 text-white' : 'bg-white text-black hover:bg-gray-100'}`}
                  >
                    {isBatchMode ? 'Cancel Batch Mode' : 'Batch Print QR Codes'}
                  </button>
                  <button
                    onClick={() => openForm()}
                    className="px-4 py-2 bg-yellow-500 text-black rounded-md hover:bg-yellow-600"
                  >
                    Add New Item
                  </button>
                </div>
              )}
            </div>
            
            {/* Tab Navigation */}
            <div className="flex space-x-1 border-b border-gray-600">
              <button
                onClick={() => setActiveTab('items')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'items'
                    ? 'text-yellow-400 border-b-2 border-yellow-400'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Items & Equipment
              </button>
              <button
                onClick={() => setActiveTab('supplies')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'supplies'
                    ? 'text-yellow-400 border-b-2 border-yellow-400'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Supply Management
              </button>
            </div>
          </div>
          
          {/* Success/Error Messages */}
          {success && (
            <div className="m-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-md">
              {success}
            </div>
          )}
          
          {error && (
            <div className="m-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md">
              {error}
            </div>
          )}
          
          {/* Tab Content */}
          {activeTab === 'items' && (
            <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Manage Inventory Items</h2>
              {isBatchMode && selectedItems.length > 0 && (
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">{selectedItems.length} item(s) selected</span>
                  <button
                    onClick={(e) => {
                    e.stopPropagation();
                    handleBatchPrintQRCode(selectedItems);
                  }}
                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                  >
                    Print Selected ({selectedItems.length})
                  </button>
                </div>
              )}
            </div>
            
            {loading && !items.length ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Loading items...</p>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-600">No items in inventory. Add your first item!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((item) => (
                      <tr 
                        key={item.id} 
                        className={`hover:bg-gray-50 ${isBatchMode && selectedItems.some(selected => selected.id === item.id) ? 'bg-blue-50' : ''}`}
                        onClick={() => isBatchMode && toggleItemSelection(item)}
                        style={{ cursor: isBatchMode ? 'pointer' : 'default' }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {isBatchMode && (
                              <div className="mr-3">
                                <input
                                  type="checkbox"
                                  checked={selectedItems.some(selected => selected.id === item.id)}
                                  onChange={() => {}}
                                  className="h-4 w-4 text-blue-600 rounded"
                                  style={{ pointerEvents: 'none' }} // Prevent double toggle
                                />
                              </div>
                            )}
                            <div className="h-10 w-10 flex-shrink-0">
                              {item.imageUrl ? (
                                <img
                                  className="h-10 w-10 rounded-full object-cover"
                                  src={item.imageUrl}
                                  alt={item.name}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <span className="text-gray-500 text-xs">No img</span>
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{item.name}</div>
                              <div className="text-sm text-gray-500">ID: {item.id.substring(0, 8)}...</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.category || 'Uncategorized'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            item.status === 'Available' 
                              ? 'bg-green-100 text-green-800' 
                              : item.status === 'Checked Out' 
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {item.status}
                          </span>
                          {item.status === 'Checked Out' && (
                            <div className="text-xs text-gray-500 mt-1">
                              By: {item.currentHolderName || 'Unknown'}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-3">
                            <button 
                              onClick={() => handlePrintQRCode(item)}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors shadow-sm"
                            >
                              Print QR
                            </button>
                            <button 
                              onClick={() => handleDownloadQRCode(item)}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                            >
                              Download QR
                            </button>
                            <button
                              onClick={() => openForm(item)}
                              className="px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          )}

          {/* Supply Management Tab */}
          {activeTab === 'supplies' && (
            <div className="p-6">
              <SupplyManagementDashboard 
                onAddConsumable={() => {
                  setActiveTab('items');
                  setTimeout(() => {
                    openForm();
                    // Pre-select consumable type
                    setFormData(prev => ({ ...prev, itemType: 'consumable' }));
                  }, 100);
                }}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Add/Edit Item Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full max-h-90vh overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {selectedItem ? 'Edit Item' : 'Add New Item'}
                </h2>
                <button
                  onClick={closeForm}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Item Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                      Category
                    </label>
                    <input
                      type="text"
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows="3"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="itemType" className="block text-sm font-medium text-gray-700">
                      Item Type
                    </label>
                    <select
                      id="itemType"
                      name="itemType"
                      value={formData.itemType}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                    >
                      <option value="equipment">Equipment</option>
                      <option value="consumable">Consumable</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                      disabled={selectedItem && selectedItem.status === 'Checked Out'}
                    >
                      <option value="Available">Available</option>
                      <option value="Under Maintenance">Under Maintenance</option>
                      {selectedItem && selectedItem.status === 'Checked Out' && (
                        <option value="Checked Out">Checked Out</option>
                      )}
                    </select>
                    {selectedItem && selectedItem.status === 'Checked Out' && (
                      <p className="text-xs text-gray-500 mt-1">
                        Status cannot be changed while item is checked out.
                      </p>
                    )}
                  </div>

                  {/* Consumable-specific fields */}
                  {formData.itemType === 'consumable' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="initialStock" className="block text-sm font-medium text-gray-700">
                            Initial Stock *
                          </label>
                          <input
                            type="number"
                            id="initialStock"
                            name="initialStock"
                            value={formData.initialStock}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                            min="0"
                            step="0.01"
                            required={formData.itemType === 'consumable'}
                          />
                        </div>
                        <div>
                          <label htmlFor="currentStock" className="block text-sm font-medium text-gray-700">
                            Current Stock *
                          </label>
                          <input
                            type="number"
                            id="currentStock"
                            name="currentStock"
                            value={formData.currentStock}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                            min="0"
                            step="0.01"
                            required={formData.itemType === 'consumable'}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="unit" className="block text-sm font-medium text-gray-700">
                            Unit of Measurement *
                          </label>
                          <input
                            type="text"
                            id="unit"
                            name="unit"
                            value={formData.unit}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                            placeholder="e.g., ml, pieces, kg, liters"
                            required={formData.itemType === 'consumable'}
                          />
                        </div>
                        <div>
                          <label htmlFor="lowStockAlert" className="block text-sm font-medium text-gray-700">
                            Low Stock Alert Level
                          </label>
                          <input
                            type="number"
                            id="lowStockAlert"
                            name="lowStockAlert"
                            value={formData.lowStockAlert}
                            onChange={handleInputChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                            min="0"
                            step="0.01"
                            placeholder="Alert when stock falls below this level"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label htmlFor="image" className="block text-sm font-medium text-gray-700">
                      Item Image
                    </label>
                    <input
                      type="file"
                      id="image"
                      name="image"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                    />
                    {selectedItem && selectedItem.imageUrl && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">Current image:</p>
                        <img 
                          src={selectedItem.imageUrl} 
                          alt={selectedItem.name} 
                          className="mt-1 h-20 w-auto object-cover rounded-md" 
                        />
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={closeForm}
                    className="mr-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : selectedItem ? 'Update Item' : 'Create Item'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


      {/* QR Code Size Selector Modal */}
      {showQrSizeSelector && (
        <QRCodeSizeSelector
          item={currentQrItem || { id: 'batch', name: 'Selected Items' }}
          onGenerate={handleGenerateQRCode}
          onClose={() => {
            setShowQrSizeSelector(false);
            setCurrentQrItem(null);
            batchQrCodesRef.current = [];
          }}
          actionType={qrCodeAction?.includes('print') ? 'print' : 'download'}
        />
      )}
    </Layout>
  );
};

export default AdminPanel;
